/**
 * Operator Switcher Component
 * Dropdown to switch between operators within the kiosk customizer
 */

import { useState, useEffect } from 'react';
import { Check, ChevronsUpDown, Building2 } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/core/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/core/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/core/components/ui/tooltip';
import { useOperatorStore } from '@/core/stores/operator-store';
import { cn } from '@/lib/utils';

export interface OperatorSwitcherProps {
  /** Current operator ID */
  currentOperatorId: string | null;

  /** Current operator name */
  currentOperatorName: string | null;

  /** Handler for operator change */
  onOperatorChange: (operatorId: string, operatorName: string) => void;

  /** Whether the switcher is disabled */
  disabled?: boolean;
}

/**
 * Operator switcher dropdown component
 * Allows switching between available operators
 */
export function OperatorSwitcher({
  currentOperatorId,
  currentOperatorName,
  onOperatorChange,
  disabled = false,
}: OperatorSwitcherProps) {
  const [open, setOpen] = useState(false);
  const { operators, loadOperators, isLoading } = useOperatorStore();

  // Load operators on mount if not already loaded
  useEffect(() => {
    if (operators.length === 0 && !isLoading) {
      loadOperators();
    }
  }, [operators.length, isLoading, loadOperators]);

  // Filter out current operator and show available operators
  const availableOperators = operators.filter(
    (op) => op.operatorId.toString() !== currentOperatorId
  );

  // Only disable if explicitly disabled (loading/saving) or no operators available to switch to
  const isDisabled = disabled || (operators.length <= 1);

  // Tooltip message for disabled state
  const getTooltipMessage = () => {
    if (isLoading) return 'Loading operators...';
    if (disabled) return 'Cannot switch operators while loading or saving';
    if (operators.length === 0) return 'No operators available';
    if (operators.length === 1) return 'Only one operator available';
    return 'Switch to a different operator';
  };

  // Debug log
  console.log('[OperatorSwitcher]', {
    operators: operators.length,
    isLoading,
    disabled,
    isDisabled,
    currentOperatorId,
    currentOperatorName,
  });

  const switcherButton = (
    <Button
      variant="outline"
      role="combobox"
      aria-expanded={open}
      aria-label="Select operator"
      className="w-[280px] justify-between"
      disabled={isDisabled}
    >
      <div className="flex items-center gap-2 truncate">
        <Building2 className="h-4 w-4 shrink-0 opacity-50" />
        <span className="truncate">
          {currentOperatorName || 'Select operator...'}
        </span>
      </div>
      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <Popover open={open} onOpenChange={setOpen}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              {switcherButton}
            </PopoverTrigger>
          </TooltipTrigger>
      <PopoverContent className="w-[280px] p-0">
        <Command>
          <CommandInput placeholder="Search operators..." />
          <CommandEmpty>No operators found.</CommandEmpty>
          <CommandGroup>
            {/* Current operator (highlighted) */}
            {currentOperatorId && currentOperatorName && (
              <CommandItem
                key={`current-${currentOperatorId}`}
                value={currentOperatorName}
                onSelect={() => {
                  setOpen(false);
                }}
                className="cursor-default"
              >
                <Check className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span className="font-medium">{currentOperatorName}</span>
                  <span className="text-xs text-muted-foreground">
                    ID: {currentOperatorId}
                  </span>
                </div>
              </CommandItem>
            )}

            {/* Available operators */}
            {availableOperators.map((operator) => (
              <CommandItem
                key={operator.operatorId}
                value={operator.name}
                onSelect={() => {
                  onOperatorChange(
                    operator.operatorId.toString(),
                    operator.name
                  );
                  setOpen(false);
                }}
              >
                <div className="mr-2 h-4 w-4" /> {/* Spacer for alignment */}
                <div className="flex flex-col">
                  <span>{operator.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ID: {operator.operatorId}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
        </Popover>
        <TooltipContent>
          <p>{getTooltipMessage()}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
